import React, { useCallback, useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import type { Row } from '@tanstack/react-table'
import { DotsHorizontalIcon, TrashIcon } from '@radix-ui/react-icons'
import { Loader2, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import Skeleton from '@/components/ui/skeleton'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import MyPagination from 'components/ui/MyPagination'
import { MyImage } from 'components/MyImage'
import { cdnUrl } from 'commons/cdn'
import { formatTime, selectFileReader } from 'commons'
import { getFetcher, postFetcher } from 'api/swr'

type SourceType = 'avatar' | 'template'
type Status = 'running' | 'ready' | 'delivered' | 'failed'

type Preview = {
  sourceType: SourceType
  sourceId: string
  sourceImageUrl: string
  displayUrl: string
}

type CopyPair = { title: string; subTitle: string }

type PushMaterial = {
  id: string
  sourceType: SourceType
  sourceId: string
  sourceImageUrl: string
  generatedImageUrl?: string
  copies?: { US: CopyPair; BR: CopyPair; MX: CopyPair }
  status?: Status
  progress?: string
  error?: string
  pushServerError?: string
  operatorName?: string
  imageModelRole?: 'primary' | 'backup'
  createTime?: number
}

type ListResponse = {
  items: PushMaterial[]
  hasMore?: boolean
  pageEndId?: string | null
}

type ConfigResponse = {
  defaultImagePrompt?: string
  defaultCopyPrompt?: string
}

const PAGE_SIZE = 20
const IMAGE_PROMPT_KEY = 'push-materials.imagePrompt.v3'
const COPY_PROMPT_KEY = 'push-materials.copyPrompt.v3'
const COUNTRIES = ['US', 'BR', 'MX'] as const

function inferSourceType(id: string): SourceType {
  return id.trim().length <= 8 ? 'template' : 'avatar'
}

function isBusy(item: PushMaterial) {
  return item.status === 'running'
}

function imageModelLabel(role?: string) {
  if (role === 'primary') return '主要'
  if (role === 'backup') return '备用'
  return ''
}

const listKey = (after: string | null) => [
  '/push-materials',
  { limit: PAGE_SIZE, ...(after ? { after } : {}) },
]

export default function PushMaterialPrepTab() {
  const { toast } = useToast()
  const [after, setAfter] = useState<string | null>(null)
  const [afters, setAfters] = useState<string[]>([])
  const [createOpen, setCreateOpen] = useState(false)

  const { data, error, isLoading, mutate } = useSWR<ListResponse>(
    listKey(after),
    fetcherList,
    {
      refreshInterval: (latest) =>
        latest?.items?.some(isBusy) ? 2500 : 0,
      dedupingInterval: 1000,
    },
  )

  const goAfter = useCallback((nextAfter: string | null) => {
    if (nextAfter === null) {
      setAfter(null)
      setAfters([])
      return
    }
    setAfters((prev) => {
      const index = prev.findIndex((item) => item === nextAfter)
      if (index === -1) return [...prev, nextAfter]
      return prev.slice(0, index + 1)
    })
    setAfter(nextAfter)
  }, [])

  const nextAfter =
    data?.hasMore && data.pageEndId ? data.pageEndId : undefined

  const onCreated = (item: PushMaterial) => {
    goAfter(null)
    mutate(
      (current) => ({
        items: [
          item,
          ...(current?.items || []).filter((row) => row.id !== item.id),
        ].slice(0, PAGE_SIZE),
        hasMore: current?.hasMore || false,
        pageEndId: current?.pageEndId || null,
      }),
      { revalidate: true },
    )
  }

  const onDelete = useCallback(async (id: string) => {
    if (!window.confirm('删除这条素材？')) return
    try {
      await postFetcher(`/push-materials/${id}/delete`, { arg: {} })
      mutate()
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '删除失败',
        description: err?.message || '未知错误',
      })
    }
  }, [mutate, toast])

  const columns = useMemo(
    () => [
      {
        header: '#',
        cell: ({ row }: { row: Row<PushMaterial> }) => row.index + 1,
      },
      {
        header: '原图',
        cell: ({ row }: { row: Row<PushMaterial> }) => {
          const url = row.original.sourceImageUrl
          if (!url) return <Skeleton className="h-24 w-24 rounded-xl" />
          return (
            <MyImage
              alt="source"
              className="aspect-square h-24 w-24 rounded-md border border-border object-contain"
              src={cdnUrl(url)}
            />
          )
        },
      },
      {
        header: '成图',
        cell: ({ row }: { row: Row<PushMaterial> }) => {
          const url = row.original.generatedImageUrl
          if (!url) {
            return isBusy(row.original) ? (
              <div className="flex h-24 w-24 items-center justify-center rounded-md border border-border">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Skeleton className="h-24 w-24 rounded-xl" />
            )
          }
          return (
            <MyImage
              alt="generated"
              className="aspect-square h-24 w-24 rounded-md border border-border object-contain"
              src={cdnUrl(url)}
            />
          )
        },
      },
      {
        header: '生图模型',
        cell: ({ row }: { row: Row<PushMaterial> }) => {
          const model = imageModelLabel(row.original.imageModelRole)
          return (
            <div className="text-sm">{model || '—'}</div>
          )
        },
      },
      {
        header: '文案',
        cell: ({ row }: { row: Row<PushMaterial> }) => {
          const copies = row.original.copies
          if (!copies?.US?.title) {
            return <span className="text-xs text-muted-foreground">—</span>
          }
          return (
            <div className="min-w-[220px] space-y-1">
              {COUNTRIES.map((country) => (
                <div key={country} className="text-xs">
                  <span className="mr-1 font-medium text-muted-foreground">
                    {country}
                  </span>
                  <span>{copies[country].title}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    / {copies[country].subTitle}
                  </span>
                </div>
              ))}
            </div>
          )
        },
      },
      {
        header: '进度',
        cell: ({ row }: { row: Row<PushMaterial> }) => {
          const item = row.original
          if (isBusy(item)) {
            return (
              <div className="flex max-w-[280px] items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                <span>{item.progress || '处理中'}</span>
              </div>
            )
          }
          if (item.status === 'failed') {
            return (
              <div className="max-w-[320px]">
                <Badge variant="destructive">失败</Badge>
                <div className="mt-1 break-words text-xs text-destructive">
                  {item.pushServerError || item.error}
                </div>
              </div>
            )
          }
          if (item.status === 'delivered') {
            return <Badge variant="secondary">已投递</Badge>
          }
          return <Badge variant="secondary">{item.progress || '完成'}</Badge>
        },
      },
      {
        header: '操作人',
        cell: ({ row }: { row: Row<PushMaterial> }) => (
          <div className="text-sm">{row.original.operatorName || '—'}</div>
        ),
      },
      {
        header: '时间',
        cell: ({ row }: { row: Row<PushMaterial> }) =>
          row.original.createTime ? (
            <div className="text-xs text-muted-foreground">
              {formatTime(row.original.createTime)?.[0]}
            </div>
          ) : (
            '—'
          ),
      },
      {
        header: '操作',
        cell: ({ row }: { row: Row<PushMaterial> }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
              >
                <DotsHorizontalIcon className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500"
                onSelect={() => onDelete(row.original.id)}
              >
                <TrashIcon className="mr-2 h-4 w-4" />
                <span>删除</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [onDelete],
  )

  const table = useReactTable({
    data: data?.items || [],
    columns,
    enableRowSelection: false,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  })

  if (isLoading && !data) return <Loader>Load push materials</Loader>
  if (!data || error) return <EmptyDatas />

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 flex shrink-0 items-center justify-between gap-4">
        <MyPagination
          nextAfter={nextAfter}
          after={after}
          goAfter={goAfter}
          afters={afters}
          mutate={mutate}
          className="w-auto m-0"
        />
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          新建素材
        </Button>
      </div>
      <div className="min-h-0 flex-1">
        <Card className="flex h-full flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {data.items.length === 0 ? <EmptyDatas /> : <Table table={table} />}
            </div>
          </CardContent>
        </Card>
      </div>
      <CreateMaterialDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={onCreated}
      />
    </div>
  )
}

function CreateMaterialDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (item: PushMaterial) => void
}) {
  const { toast } = useToast()
  const [sourceId, setSourceId] = useState('')
  const [preview, setPreview] = useState<Preview | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewError, setPreviewError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [mode, setMode] = useState<'generate' | 'manual'>('generate')
  const [imagePrompt, setImagePrompt] = useState('')
  const [copyPrompt, setCopyPrompt] = useState('')
  const [image, setImage] = useState<{ dataUrl: string; name: string } | null>(
    null,
  )
  const [fileKey, setFileKey] = useState(0)
  const [copies, setCopies] = useState<Record<(typeof COUNTRIES)[number], CopyPair>>(
    () => ({
      US: { title: '', subTitle: '' },
      BR: { title: '', subTitle: '' },
      MX: { title: '', subTitle: '' },
    }),
  )
  const { data: config } = useSWR<ConfigResponse>(
    open ? '/push-materials/config' : null,
    getFetcher,
  )

  const trimmed = sourceId.trim()
  const sourceType = inferSourceType(trimmed)

  useEffect(() => {
    if (!open || !config) return
    setImagePrompt(
      window.localStorage.getItem(IMAGE_PROMPT_KEY) ||
        config.defaultImagePrompt ||
        '',
    )
    setCopyPrompt(
      window.localStorage.getItem(COPY_PROMPT_KEY) ||
        config.defaultCopyPrompt ||
        '',
    )
  }, [open, config])

  const updateImagePrompt = (value: string) => {
    setImagePrompt(value)
    window.localStorage.setItem(IMAGE_PROMPT_KEY, value)
  }

  const updateCopyPrompt = (value: string) => {
    setCopyPrompt(value)
    window.localStorage.setItem(COPY_PROMPT_KEY, value)
  }

  useEffect(() => {
    if (!open) return
    if (!trimmed) {
      setPreview(null)
      setPreviewError('')
      setPreviewing(false)
      return
    }
    let cancelled = false
    setPreviewing(true)
    const timer = window.setTimeout(async () => {
      try {
        const result = await previewById(trimmed)
        if (!cancelled) {
          setPreview(result)
          setPreviewError('')
        }
      } catch (err: any) {
        if (!cancelled) {
          setPreview(null)
          setPreviewError(err?.message || '找不到对应图片')
        }
      } finally {
        if (!cancelled) setPreviewing(false)
      }
    }, 400)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [open, trimmed])

  const resetSource = () => {
    setSourceId('')
    setPreview(null)
    setPreviewError('')
    setPreviewing(false)
    setSubmitting(false)
    setMode('generate')
    setImage(null)
    setFileKey((current) => current + 1)
    setCopies({
      US: { title: '', subTitle: '' },
      BR: { title: '', subTitle: '' },
      MX: { title: '', subTitle: '' },
    })
  }

  const hasManualCopy = COUNTRIES.some((country) => copies[country].title.trim())

  const onPickImage = async (file?: File) => {
    if (!file) {
      setImage(null)
      return
    }
    try {
      const data = (await selectFileReader(file)) as {
        original: string
        name: string
      }
      setImage({ dataUrl: data.original, name: data.name || file.name })
    } catch (err: any) {
      setImage(null)
      toast({
        variant: 'destructive',
        title: '图片读取失败',
        description: err?.message || '请换一张图片',
      })
    }
  }

  const onSubmit = async () => {
    if (!preview) {
      toast({ variant: 'destructive', title: '请先粘贴有效 ID' })
      return
    }
    if (mode === 'manual') {
      if (!image) {
        toast({ variant: 'destructive', title: '请上传成图' })
        return
      }
      if (!hasManualCopy) {
        toast({ variant: 'destructive', title: '至少填写一国标题' })
        return
      }
    } else if (!imagePrompt.trim() || !copyPrompt.trim()) {
      toast({ variant: 'destructive', title: '请填写生图和文案提示词' })
      return
    }
    try {
      setSubmitting(true)
      const created =
        mode === 'manual'
          ? await postFetcher('/push-materials/deliver', {
              arg: {
                sourceId: preview.sourceId,
                sourceType: preview.sourceType,
                image: image?.dataUrl,
                copies,
              },
            })
          : await postFetcher('/push-materials/generate', {
              arg: {
                sourceId: preview.sourceId,
                sourceType: preview.sourceType,
                imagePrompt,
                copyPrompt,
              },
            })
      toast({
        title: '任务已提交',
        description:
          mode === 'manual'
            ? '已交给运营 worker 投进 push 内容库。'
            : '关闭页面或刷新也可以，完成后会出现在表格里。',
        className: 'bg-green-500 text-white',
      })
      onOpenChange(false)
      resetSource()
      if (mode === 'generate') onCreated(created)
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: '提交失败',
        description: err?.message || '未知错误',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) resetSource()
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>新建 push 素材</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>模板或头像 ID</Label>
            <Input
              autoFocus
              value={sourceId}
              onChange={(event) => setSourceId(event.target.value)}
              placeholder="粘贴后自动识别并拉图"
            />
            {preview ? (
              <p className="text-xs text-muted-foreground">
                已识别为{preview.sourceType === 'template' ? '模板' : '头像'} · {preview.sourceId}
              </p>
            ) : trimmed ? (
              <p className="text-xs text-muted-foreground">
                将按{sourceType === 'template' ? '模板' : '头像'}查找
                （长度 {trimmed.length}
                {sourceType === 'template' ? ' ≤ 8' : ' > 8'}）
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                长度 ≤ 8 按模板 ID，否则按头像 ID。找不到会再试另一种。
              </p>
            )}
          </div>
          {previewing ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              正在获取图片…
            </div>
          ) : null}
          {previewError ? (
            <p className="text-sm text-destructive">{previewError}</p>
          ) : null}
          {preview ? (
            <div className="flex items-center gap-3">
              <MyImage
                alt="preview"
                className="h-24 w-24 rounded-md border border-border object-contain"
                src={cdnUrl(preview.displayUrl || preview.sourceImageUrl)}
              />
              <div className="text-sm text-muted-foreground">
                {preview.sourceType === 'template' ? '模板' : '头像'} · {preview.sourceId}
              </div>
            </div>
          ) : null}
          <Tabs
            value={mode}
            onValueChange={(value) => setMode(value as 'generate' | 'manual')}
          >
            <TabsList>
              <TabsTrigger value="generate">造物台生成</TabsTrigger>
              <TabsTrigger value="manual">自己填写</TabsTrigger>
            </TabsList>
            <TabsContent value="generate" className="space-y-4">
              <div className="space-y-2">
                <Label>生图提示词</Label>
                <Textarea
                  value={imagePrompt}
                  onChange={(event) => updateImagePrompt(event.target.value)}
                  className="min-h-[140px] font-mono text-xs"
                  placeholder="这次怎么出图"
                />
              </div>
              <div className="space-y-2">
                <Label>文案提示词</Label>
                <Textarea
                  value={copyPrompt}
                  onChange={(event) => updateCopyPrompt(event.target.value)}
                  className="min-h-[100px] font-mono text-xs"
                  placeholder="这次怎么写文案"
                />
              </div>
            </TabsContent>
            <TabsContent value="manual" className="space-y-4">
              <p className="text-xs text-muted-foreground">
                直接投进 push 内容库，不记在这张任务表里。副标题留空时，正文用标题。
              </p>
              <div className="space-y-2">
                <Label>成图</Label>
                <Input
                  key={fileKey}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  onChange={(event) => onPickImage(event.target.files?.[0])}
                />
                {image ? (
                  <div className="flex items-center gap-3">
                    <img
                      alt="upload"
                      className="h-24 w-24 rounded-md border border-border object-contain"
                      src={image.dataUrl}
                    />
                    <div className="text-sm text-muted-foreground">{image.name}</div>
                  </div>
                ) : null}
              </div>
              {COUNTRIES.map((country) => (
                <div key={country} className="grid grid-cols-[48px_1fr_1fr] items-center gap-2">
                  <div className="text-sm font-medium">{country}</div>
                  <Input
                    value={copies[country].title}
                    placeholder="标题"
                    onChange={(event) =>
                      setCopies((current) => ({
                        ...current,
                        [country]: {
                          ...current[country],
                          title: event.target.value,
                        },
                      }))
                    }
                  />
                  <Input
                    value={copies[country].subTitle}
                    placeholder="副标题"
                    onChange={(event) =>
                      setCopies((current) => ({
                        ...current,
                        [country]: {
                          ...current[country],
                          subTitle: event.target.value,
                        },
                      }))
                    }
                  />
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetSource()
              onOpenChange(false)
            }}
            disabled={submitting}
          >
            取消
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={
              submitting ||
              !preview ||
              (mode === 'manual'
                ? !image || !hasManualCopy
                : !imagePrompt.trim() || !copyPrompt.trim())
            }
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            {mode === 'manual' ? '投递到内容库' : '开始准备素材'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

async function previewById(id: string): Promise<Preview> {
  const preferred = inferSourceType(id)
  const fallback: SourceType = preferred === 'template' ? 'avatar' : 'template'
  try {
    return await getFetcher('/push-materials/preview', {
      sourceId: id,
      sourceType: preferred,
    })
  } catch (first) {
    try {
      return await getFetcher('/push-materials/preview', {
        sourceId: id,
        sourceType: fallback,
      })
    } catch {
      throw first
    }
  }
}

async function fetcherList([endpoint, params]: [string, Record<string, unknown>]) {
  return getFetcher(endpoint, params)
}
